import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type BillingMachineResource,
  BillingMachinesController_rename_v1,
  type BillingMachinesController_rename_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { machineNameSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';

/**
 * Renaming a machine: `PATCH /v1/billing-machines/{id}`, owner **and manager** —
 * the one write on the machine screen a manager may make (#341).
 *
 * **The body is `{ name }` and nothing else.** The machine ID is what an import
 * matches a bill on, so it is not editable and `RenameBillingMachineDto` has no
 * field for it; a body naming it is refused `VALIDATION_UNKNOWN_FIELD`. It is
 * shown as text beside the name, never in an input a save could carry.
 *
 * **Save is not disabled when the name is unchanged.** Renaming a machine to the
 * name it has is how a name changed outside the API gets its displayed name
 * repaired — the API renames the machine's actor in the same write.
 */

/**
 * `VALIDATION_FAILED` on `name`. The server does not say which bound it broke,
 * so this states both rather than guessing one.
 */
const NAME_REFUSED = 'A name is 1 to 100 characters.';

const renameSchema = z.object({ name: machineNameSchema });

type RenameValues = z.infer<typeof renameSchema>;

/** Whether a `VALIDATION_FAILED` names this field in `details.fields[].field`. */
function namesField(error: ApiError, field: string): boolean {
  const fields = error.details?.fields;

  return (
    Array.isArray(fields) &&
    fields.some(
      (entry: unknown) =>
        typeof entry === 'object' &&
        entry !== null &&
        (entry as { field?: unknown }).field === field,
    )
  );
}

export function RenameMachineDialog({
  machine,
  onClose,
  onRenamed,
}: {
  /** The machine being renamed, or `null` when the dialog is shut. */
  machine: BillingMachineResource | null;
  onClose: () => void;
  onRenamed: (machine: BillingMachineResource) => void;
}) {
  return (
    <Dialog
      open={machine !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Rename machine</DialogTitle>
          <DialogDescription>
            Its bills, past and future, are shown under the new name. The machine ID, the outlet it
            sells for and its product codes stay as they are.
          </DialogDescription>
        </DialogHeader>

        {machine !== null && <RenameMachineForm machine={machine} onRenamed={onRenamed} />}
      </FormDialogContent>
    </Dialog>
  );
}

function RenameMachineForm({
  machine,
  onRenamed,
}: {
  machine: BillingMachineResource;
  onRenamed: (machine: BillingMachineResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(renameSchema),
    mode: 'onTouched',
    defaultValues: { name: machine.name },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // The key belongs to the body, as on the move: a retry after a lost answer
  // replays, a corrected name gets a fresh key. See `lib/write-attempt.ts`.
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async ({ name }: RenameValues) => {
    if (pending) return;

    setFormError(null);

    try {
      const body: BillingMachinesController_rename_v1Body = { name };
      const sending = attemptFor(attempt.current, JSON.stringify(body));
      attempt.current = sending;

      onRenamed(
        await client.request(BillingMachinesController_rename_v1, {
          path: { id: machine.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === 'VALIDATION_FAILED' &&
        namesField(error, 'name')
      ) {
        form.setError('name', { message: NAME_REFUSED });
        form.setFocus('name');
      } else {
        // `RESOURCE_NOT_FOUND` included: no such machine and one out of reach
        // are one answer on the API, and its one message says so.
        setFormError(messageForFailure(error));
      }
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <div className="space-y-1.5 rounded-2xl bg-muted px-4 py-3">
          <p className={fieldLabel}>Machine ID</p>
          <p className="font-mono text-foreground numeric">{machine.machineCode}</p>
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Name
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    required
                    placeholder="Kids Corner machine"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormDescription>
                The machine ID cannot change — every import matches its bills on it.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save name'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
